import {
  buildCustomerRow,
  isEmptyRow,
  matchColumns,
  validateCustomerRow,
  type BuiltRow,
  type ColumnMapping,
} from '@shared/customer-import.ts';
import { isBlankRow, parseTable, unguardCell, type TableRow } from '@shared/spreadsheet.ts';
import { readXlsx, XlsxError, type XlsxErrorCode } from '@shared/xlsx.ts';

/**
 * Getting somebody's customer list out of whatever they have and into a grid.
 *
 * All of this runs in the browser, before anything is sent. That is the whole
 * reason the import feels the way it does: the file never makes a round trip to
 * be told its columns are wrong, the mapping and the preview are instant, and a
 * person who picked the wrong file finds out in the time it takes to read the
 * first row rather than after an upload.
 */

/**
 * The ceiling on one import, counted in rows that hold something.
 *
 * Chosen against what the preview can render and what a browser will hold, not
 * against what the server will take — the server takes a hundred rows at a time
 * and this is split across many requests. A larger list is not refused as
 * impossible, only as one file: the message says to split it.
 */
export const MAX_IMPORT_ROWS = 5000;

/**
 * How many physical lines the parser reads before giving up.
 *
 * Deliberately well above the row ceiling, because blank lines are discarded
 * *after* parsing: a file padded with separator rows would otherwise spend its
 * whole budget on nothing and quietly deliver half the customer list. Hitting
 * this cap is reported as truncation rather than taken for the end of the
 * file — at that point there is genuinely no way to tell which it was.
 */
const PARSE_LINE_CAP = MAX_IMPORT_ROWS * 4 + 1;

export type ImportFileErrorCode = XlsxErrorCode | 'legacyXls' | 'empty' | 'unreadable' | 'unterminatedQuote';

export class ImportFileError extends Error {
  constructor(readonly code: ImportFileErrorCode) {
    super(code);
    this.name = 'ImportFileError';
  }
}

/**
 * One row of the source, with the line it came from.
 *
 * The line number is carried rather than recomputed because blank rows are
 * dropped on the way in: counting positions in the compacted list would report
 * every later problem against the wrong line of the person's file, which is
 * precisely what the line numbers exist to prevent.
 */
export type SourceRow = TableRow;

export interface ImportSheet {
  name: string;
  rows: SourceRow[];
}

export interface ImportSource {
  /** What to call this on screen — a file name, or nothing for a paste. */
  label: string;
  sheets: ImportSheet[];
  /** True when there was more of this file than one import will carry. */
  truncated: boolean;
}

/**
 * Reads a dropped or chosen file into sheets of cells.
 *
 * The format is decided by what is actually in the file rather than by its
 * extension: people rename things, and a `.csv` that is really a workbook (or
 * the other way round) is common enough that trusting the name would be the
 * first thing to break.
 */
export async function readImportFile(file: File): Promise<ImportSource> {
  const buffer = await file.arrayBuffer();
  const head = new Uint8Array(buffer.slice(0, 8));

  // "PK" — a ZIP, which for our purposes is an xlsx.
  if (head[0] === 0x50 && head[1] === 0x4b) {
    let sheets;
    try {
      sheets = await readXlsx(buffer, { maxRows: PARSE_LINE_CAP });
    } catch (error) {
      throw error instanceof XlsxError ? new ImportFileError(error.code) : new ImportFileError('unreadable');
    }
    // The rows already carry their own line numbers, and those are the ones the
    // person sees down the side of their spreadsheet. Nor is anything unguarded
    // here: the apostrophe guard is a delimited-file convention, and an xlsx
    // stores text as text, so stripping one would delete a character somebody
    // actually typed.
    return finish(
      file.name,
      sheets,
      sheets.some((sheet) => sheet.rows.length >= PARSE_LINE_CAP),
    );
  }

  // An OLE compound file: Excel 97-2003. A different format entirely, and
  // saying so beats "not a valid archive" — the fix is one Save As away.
  if (head[0] === 0xd0 && head[1] === 0xcf) throw new ImportFileError('legacyXls');

  return fromText(file.name, decodeText(buffer));
}

/**
 * Text out of bytes, in whatever encoding the file is actually in.
 *
 * Decoding everything as UTF-8 is wrong in a way that does not announce itself:
 * a Windows-1252 export — which is what a great many older systems still
 * produce — turns the é of José into a replacement character, and that
 * character passes every schema and is stored as the customer's name. So the
 * bytes decide. A byte-order mark names the encoding outright; without one,
 * UTF-8 is tried strictly, and only text that is *not* valid UTF-8 falls back
 * to Windows-1252, which cannot fail because every byte is a character in it.
 */
function decodeText(buffer: ArrayBuffer): string {
  const head = new Uint8Array(buffer.slice(0, 3));

  if (head[0] === 0xff && head[1] === 0xfe) return new TextDecoder('utf-16le').decode(buffer);
  if (head[0] === 0xfe && head[1] === 0xff) return new TextDecoder('utf-16be').decode(buffer);

  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch {
    // Not UTF-8 at all. The single-byte encoding behind most CSVs that are not
    // is Windows-1252, and reading é as é beats reading it as nothing.
    return new TextDecoder('windows-1252').decode(buffer);
  }
}

/**
 * The same, for cells pasted straight out of a spreadsheet.
 *
 * The clipboard hands over tab-separated text, but people also paste a CSV they
 * opened in a text editor, so the delimiter is sniffed rather than assumed.
 */
export function readPastedRows(text: string): ImportSource {
  return fromText('', text);
}

function fromText(label: string, text: string): ImportSource {
  const parsed = parseTable(text, undefined, { maxRows: PARSE_LINE_CAP });
  // Everything past an unclosed quote is sitting in one cell rather than in the
  // rows it belongs to, so there is no honest way to show this as a table.
  if (parsed.unterminatedQuote) throw new ImportFileError('unterminatedQuote');
  return finish(label, [{ name: label, rows: numbered(parsed.rows) }], parsed.stoppedEarly);
}

/**
 * Tags each parsed row with its line, before any of them are dropped.
 *
 * Delimited text has no gaps by construction — one line, one row — so its
 * position is its line number. A worksheet is not like that, which is why its
 * reader carries the number instead of this recomputing one.
 */
function numbered(rows: readonly string[][]): SourceRow[] {
  return rows.map((cells, index) => ({ line: index + 1, cells: cells.map(unguardCell) }));
}

function finish(label: string, sheets: ImportSheet[], stoppedEarly: boolean): ImportSource {
  let overLimit = false;
  const cleaned = sheets.map((sheet) => {
    // Blank lines between sections, and the empty rows a selection picks up
    // below the last real one, are not data and are not failures either. Their
    // line numbers still belong to the rows that follow them.
    const rows = sheet.rows.filter((row) => !isBlankRow(row.cells));
    if (rows.length > MAX_IMPORT_ROWS) overLimit = true;
    return { name: sheet.name, rows: rows.slice(0, MAX_IMPORT_ROWS) };
  });

  if (cleaned.every((sheet) => sheet.rows.length === 0)) throw new ImportFileError('empty');
  return { label, sheets: cleaned, truncated: overLimit || stoppedEarly };
}

/**
 * Whether the first row names the columns rather than being one.
 *
 * The test is simply whether anything in it is recognizable as a column name.
 * A header row is the one row in the file written for a human to read, so if
 * none of its cells means anything to the matcher, it is far likelier to be
 * Dana Okafor than a row of labels.
 */
export function looksLikeHeaderRow(row: SourceRow | undefined): boolean {
  if (!row || row.cells.length === 0) return false;
  return matchColumns(row.cells).some((match) => match !== null);
}

/** The widest row in the sheet — how many columns there are to map. */
export function columnCount(rows: readonly SourceRow[]): number {
  return rows.reduce((widest, row) => Math.max(widest, row.cells.length), 0);
}

/**
 * Column labels to show beside each mapping control.
 *
 * A file with no header row has no names to show, so the columns are numbered
 * the way a spreadsheet numbers them — the person is looking at the same file
 * in another window, and "Column C" is what they can find there.
 */
export function columnLabels(rows: readonly SourceRow[], hasHeader: boolean, count: number): string[] {
  const header = hasHeader ? (rows[0]?.cells ?? []) : [];
  return Array.from({ length: count }, (_, index) => header[index]?.trim() || spreadsheetColumnName(index));
}

export function spreadsheetColumnName(index: number): string {
  let name = '';
  let remaining = index;
  do {
    name = String.fromCharCode(65 + (remaining % 26)) + name;
    remaining = Math.floor(remaining / 26) - 1;
  } while (remaining >= 0);
  return name;
}

export interface PreviewRow extends BuiltRow {
  /** The line in the source file, so it can be found again. */
  line: number;
  cells: string[];
  /** What the shared schema says about the row, before anything is sent. */
  fieldErrors: Record<string, string>;
  /** True when every mapped cell in the row was blank. */
  empty: boolean;
}

/**
 * Every data row, read through the current mapping.
 *
 * Recomputed whenever the mapping changes, which is what makes correcting a
 * column feel like flipping a switch rather than starting again: the preview,
 * the counts and the errors all follow from this one function.
 */
export function buildPreviewRows(
  rows: readonly SourceRow[],
  mapping: ColumnMapping,
  hasHeader: boolean,
  /** Shown against an ID cell that is not one of ours. */
  unreadableIdMessage: string,
): PreviewRow[] {
  const body = hasHeader ? rows.slice(1) : rows;

  return body.map((row) => {
    const built = buildCustomerRow(row.cells, mapping);
    const empty = isEmptyRow(built.values) && !built.id;
    // An ID that is not one of ours is a row-level error rather than a loose
    // cell: the row was naming a customer, and importing it with the id quietly
    // dropped would create a second copy of the record it meant to correct.
    const unreadableId = built.issues.some((issue) => issue.reason === 'id');

    return {
      ...built,
      line: row.line,
      cells: row.cells,
      empty,
      fieldErrors: empty
        ? // An empty row is dropped rather than reported, so it must not also
          // be marked invalid for having no name.
          {}
        : unreadableId
          ? { id: unreadableIdMessage }
          : // A row naming a customer is an edit, and an edit of a single field
            // is an ordinary thing for a spreadsheet to carry. Whether the
            // result still has a name is the server's half of that merge.
            validateCustomerRow(built.values, { update: built.id !== null }).fieldErrors,
    };
  });
}

/** The rows worth sending: not blank, and not already known to be malformed. */
export function importableRows(rows: readonly PreviewRow[]): PreviewRow[] {
  return rows.filter((row) => !row.empty && Object.keys(row.fieldErrors).length === 0);
}

export function chunk<T>(items: readonly T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) chunks.push(items.slice(index, index + size));
  return chunks;
}

/**
 * The rows that did not import, as a table to fix and feed straight back in.
 *
 * Each source row squared up to the header before its problem is appended:
 * rows are ragged wherever the file left trailing cells off, and appending to a
 * short one puts the message under somebody's City column — where re-importing
 * the corrected file reads the error text as customer data and leaves the
 * column that was supposed to hold it blank.
 */
export function problemRows(
  problems: ReadonlyArray<{ line: number; message?: string | undefined; reason?: string | undefined }>,
  rows: readonly PreviewRow[],
  labels: readonly string[],
): string[][] {
  const cellsByLine = new Map(rows.map((row) => [row.line, row.cells]));

  return problems.map((problem) => {
    const cells = [...(cellsByLine.get(problem.line) ?? [])];
    while (cells.length < labels.length) cells.push('');
    return [...cells, problem.message ?? problem.reason ?? ''];
  });
}
